<script setup lang="ts">
import { nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import type { CompanyProfile } from "@/entities/company";
import {
  Button,
  FormAlert,
  Input,
  Label,
  Textarea,
} from "@/shared/ui";

import type { FieldKey } from "../model/useSalonProfileForm";
import { useSalonProfileForm } from "../model/useSalonProfileForm";

const props = defineProps<{
  company: CompanyProfile;
}>();

const emit = defineEmits<{
  saved: [updated: CompanyProfile];
}>();

const { t } = useI18n();

const form = useSalonProfileForm({
  onSaved: (updated) => emit("saved", updated),
});

watch(
  () => props.company,
  (c) => form.seedFrom(c),
  { immediate: true },
);

const nameRef = ref<{ $el: HTMLInputElement } | null>(null);
const addressRef = ref<{ $el: HTMLInputElement } | null>(null);
const phoneRef = ref<{ $el: HTMLInputElement } | null>(null);
const descriptionRef = ref<{ $el: HTMLTextAreaElement } | null>(null);

const fieldRefs: Record<FieldKey, () => void> = {
  name: () => nameRef.value?.$el.focus(),
  address: () => addressRef.value?.$el.focus(),
  phone: () => phoneRef.value?.$el.focus(),
  description: () => descriptionRef.value?.$el.focus(),
};

async function focusInvalid(): Promise<void> {
  const field = form.firstInvalidField.value;
  if (!field) return;
  await nextTick();
  fieldRefs[field]();
}

async function onSubmit(): Promise<void> {
  await form.submit();
  await focusInvalid();
}
</script>

<template>
  <form
    class="flex flex-col gap-5"
    novalidate
    @submit.prevent="onSubmit"
  >
    <!-- form-level помилка (над кнопкою) -->
    <FormAlert
      v-if="form.fieldErrors.form"
      data-testid="salon-profile-form-error"
      variant="destructive"
      :message="t(form.fieldErrors.form)"
    />

    <!-- name (обов'язкове) -->
    <div class="flex flex-col gap-1.5">
      <Label for="salon-name">
        {{ t("salon.profile.fields.name") }}
        <span
          class="text-destructive"
          aria-hidden="true"
        >*</span>
      </Label>
      <Input
        id="salon-name"
        ref="nameRef"
        v-model="form.fields.name"
        :invalid="!!form.fieldErrors.name"
        aria-required="true"
        :aria-describedby="form.fieldErrors.name ? 'salon-name-err' : undefined"
      />
      <FormAlert
        v-if="form.fieldErrors.name"
        id="salon-name-err"
        variant="destructive"
        :message="t(form.fieldErrors.name)"
      />
    </div>

    <!-- address -->
    <div class="flex flex-col gap-1.5">
      <Label for="salon-address">{{ t("salon.profile.fields.address") }}</Label>
      <Input
        id="salon-address"
        ref="addressRef"
        v-model="form.fields.address"
        :invalid="!!form.fieldErrors.address"
        :aria-describedby="form.fieldErrors.address ? 'salon-address-err' : undefined"
        autocomplete="street-address"
      />
      <FormAlert
        v-if="form.fieldErrors.address"
        id="salon-address-err"
        variant="destructive"
        :message="t(form.fieldErrors.address)"
      />
    </div>

    <!-- phone -->
    <div class="flex flex-col gap-1.5">
      <Label for="salon-phone">{{ t("salon.profile.fields.phone") }}</Label>
      <Input
        id="salon-phone"
        ref="phoneRef"
        v-model="form.fields.phone"
        type="tel"
        inputmode="tel"
        :invalid="!!form.fieldErrors.phone"
        :aria-describedby="form.fieldErrors.phone ? 'salon-phone-err' : undefined"
        autocomplete="tel"
      />
      <FormAlert
        v-if="form.fieldErrors.phone"
        id="salon-phone-err"
        variant="destructive"
        :message="t(form.fieldErrors.phone)"
      />
    </div>

    <!-- description + char counter -->
    <div class="flex flex-col gap-1.5">
      <Label for="salon-description">{{ t("salon.profile.fields.description") }}</Label>
      <Textarea
        id="salon-description"
        ref="descriptionRef"
        v-model="form.fields.description"
        :rows="5"
        :invalid="!!form.fieldErrors.description"
        :aria-describedby="form.fieldErrors.description ? 'salon-desc-err desc-counter' : 'desc-counter'"
        :aria-invalid="!!form.fieldErrors.description"
      />
      <span
        id="desc-counter"
        class="text-xs text-muted-foreground"
      >{{ form.descriptionLength.value }}/1000</span>
      <FormAlert
        v-if="form.fieldErrors.description"
        id="salon-desc-err"
        variant="destructive"
        :message="t(form.fieldErrors.description)"
      />
    </div>

    <!-- actions -->
    <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <Button
        type="submit"
        :loading="form.submitStatus.value === 'saving'"
      >
        {{ t("salon.profile.save") }}
      </Button>
    </div>
  </form>
</template>
